import React from 'react';
import { Box, useTheme } from '@mui/material';
import { SEPPhase } from '../../shared/types/SEP';
import ArchitectureIcon from '../assets/svg/ArchitectureIcon';
import SolutionArchitectConsultantIcon from '../assets/svg/SolutionArchitectConsultantIcon';
import DefaultWorkflowIcon from '../assets/svg/DefaultWorkflowIcon';
import AIInviteOnlyIcon from '../assets/svg/AIInviteOnlyIcon';
import IdeationIcon from '../assets/svg/LightBulbIcon'; 


/**
 * Common props interface for workflow icons
 */
export interface WorkflowIconProps {
  width?: number | string;
  height?: number | string;
  color?: string;
  'aria-label'?: string;
}

/**
 * Icon component type definition
 */
type IconComponent = React.FC<WorkflowIconProps>;

/**
 * Configuration for a workflow icon with optional filled/outline variants
 */
interface WorkflowIconConfig {
  /** Default icon component (used when no variant-specific icon exists) */
  default: IconComponent;
  /** Filled variant icon (used for completed/later phases) */
  filled?: IconComponent;
  /** Outline variant icon (used for early/in-progress phases) */
  outline?: IconComponent;
}

/**
 * Mapping of workflow types to their icon configurations
 * Keys are the string values of workflow types
 */
const workflowIconMap: Record<string, WorkflowIconConfig> = {
  // SEP Design Review workflow
  'SEP Design Review': {
    default: ArchitectureIcon,
  },

  // Solution Architect Consultation workflow
  'Solution Architect Consultation': {
    default: SolutionArchitectConsultantIcon,
  },

  // IoT Lab Approval workflow (using DefaultWorkflowIcon as placeholder)
  'AI Invite Only (Restricted Use workflow)': {
    default: AIInviteOnlyIcon,
  },

  'Ideation': {
    default: IdeationIcon,
  }
};

/**
 * Default icon configuration used when workflow type is not found
 */
const defaultIconConfig: WorkflowIconConfig = {
  default: DefaultWorkflowIcon,
};

/**
 * Phases that should display filled icons (completed or later stages)
 */
const filledPhases: SEPPhase[] = [
  SEPPhase.design,
  SEPPhase.implement,
  SEPPhase.complete,
];

/**
 * Determines if a given phase should use a filled icon
 * @param phase - The current SEP phase
 * @returns true if the phase should use a filled icon variant
 */
const shouldUseFilled = (phase?: SEPPhase): boolean => {
  if (!phase) return false;
  return filledPhases.includes(phase);
};

/**
 * Retrieves the appropriate icon component based on workflow type and phase
 * 
 * @param workflowType - The workflow type string (e.g., 'SEP Design Review')
 * @param phase - Optional SEP phase to determine filled/outline variant
 * @returns The appropriate icon component for the workflow type and phase
 * 
 * @example
 * // Get icon for SEP Design Review in knockout phase (outline)
 * const Icon = getWorkflowIcon('SEP Design Review', SEPPhase.knockout);
 * 
 * @example
 * // Get icon for Solution Architect Consultation in design phase (filled)
 * const Icon = getWorkflowIcon('Solution Architect Consultation', SEPPhase.design);
 * 
 * @example
 * // Get default icon when workflow type is unknown
 * const Icon = getWorkflowIcon('Unknown Workflow');
 */
export const getWorkflowIcon = (
  workflowType?: string | null,
  phase?: SEPPhase
): IconComponent => {
  // Get the icon configuration for the workflow type, or use default
  const iconConfig = workflowType 
    ? workflowIconMap[workflowType] || defaultIconConfig
    : defaultIconConfig;

  // Determine which variant to use based on phase
  const useFilled = shouldUseFilled(phase);

  if (useFilled) {
    // Return filled variant if available, otherwise default
    return iconConfig.filled || iconConfig.default;
  } else {
    // Return outline variant if available, otherwise default
    return iconConfig.outline || iconConfig.default;
  }
};

/**
 * Retrieves the icon configuration for a workflow type
 * Useful when you need access to all icon variants
 * 
 * @param workflowType - The workflow type string
 * @returns The full icon configuration object
 */
export const getWorkflowIconConfig = (
  workflowType?: string | null
): WorkflowIconConfig => {
  if (!workflowType) return defaultIconConfig;
  return workflowIconMap[workflowType] || defaultIconConfig;
};

/**
 * Phases that should display outline style (dotted border, white background)
 */
const outlinePhases: SEPPhase[] = [
  SEPPhase.knockout,
  SEPPhase.validation,
  'Draft' as SEPPhase
];

/**
 * Determines if a given phase should use outline styling
 * @param phase - The current SEP phase
 * @returns true if the phase should use outline styling (knockout or validation)
 */
const isOutlinePhase = (phase?: SEPPhase): boolean => {
  if (!phase) return false;
  return outlinePhases.includes(phase);
};

/**
 * Props for the WorkflowIconBox component
 */
export interface WorkflowIconBoxProps {
  workflowType?: string | null;
  phase?: SEPPhase;
  colorScheme?: 'default' | 'primeBlue';
}

/**
 * A styled component that renders the workflow icon with appropriate styling based on phase
 * 
 * - knockout/validation phases: Dotted border, white background, prime blue icon
 * - Other phases: Solid white border, prime blue background, white icon
 * 
 * @example
 * <WorkflowIconBox workflowType="SEP Design Review" phase={SEPPhase.knockout} />
 */
export const WorkflowIconBox: React.FC<WorkflowIconBoxProps> = ({
  workflowType,
  phase,
  colorScheme = 'default',
}) => {
  const theme = useTheme();
  const Icon = getWorkflowIcon(workflowType, phase);
  const isOutline = isOutlinePhase(phase);
  const borderColor = colorScheme === 'primeBlue'
    ? theme.palette.prime.blue
    : isOutline
      ? theme.palette.prime.blue
      : theme.palette.content.white;
  const iconColor = colorScheme === 'primeBlue'
    ? theme.palette.prime.blue
    : isOutline
      ? theme.palette.prime.blue
      : theme.palette.content.white;

  return (
      <Box
          sx={{
              width: 34,
              height: 34,
                border: isOutline
                  ? `0.3px dashed ${borderColor}`
                  : `0.3px solid ${borderColor}`,
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: isOutline
                  ? theme.palette.content.white
                  : 'transparent',
          }}
      >
          <Box sx={{
              width: 27,
              height: 27,
              border: isOutline
                ? `0.3px dashed ${borderColor}`
                : `0.3px solid ${borderColor}`,
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: isOutline
                  ? theme.palette.content.white
                  : 'transparent',
          }}>
              <Icon
                  color={iconColor}
                  aria-label="Workflow icon"
              />

          </Box>

      </Box>
  );
};

/**
 * Checks if a workflow type has a specific icon configured
 * 
 * @param workflowType - The workflow type string
 * @returns true if the workflow type has a configured icon
 */
export const hasWorkflowIcon = (workflowType?: string | null): boolean => {
  if (!workflowType) return false;
  return workflowType in workflowIconMap;
};

export default getWorkflowIcon;
